import BaseMarker from "./BaseMarker";
import * as THREE from 'three';
import { markRaw } from 'vue';
import { createShapeGeometry, DEFAULT_MARKER_SHAPE, DEFAULT_MARKER_COLOR } from '@/lib/markerShapeFactory';
import { getMarkerStyle, getMarkerStyleById } from '@/lib/markerStyleRegistry';

export default class AnomalyMarker extends BaseMarker {
    label = null;
    name = null;
    externalReference = null;
    markerTypeId = null;
    status = null;
    isAIGenerated = false;
    inspectionAreaId = null;
    thumbnailPath = null;
    defaultColor = null;
    createdOn = null;
    isActive = false;
    shape = null;
    color = null;

    constructor(marker){
        super(marker.id, marker.type, marker.assetId, marker.scanId, marker.transform, marker.eTag);

        this.label = marker.label;
        this.name = marker.name ?? null;
        this.externalReference = marker.externalReference ?? null;
        this.markerTypeId = marker.markerTypeId || null;
        this.status = marker.status;
        this.isAIGenerated = marker.isAIGenerated;
        this.inspectionAreaId = marker.inspectionAreaId;
        this.createdOn = marker.createdOn;
        this.thumbnailPath = marker.thumbnailPath;
        this.isActive = marker.isActive;

        // Resolve render style. Precedence: an explicit style on the marker DTO → the category
        // matched by stored markerTypeId (stable) → the category matched by label (legacy markers
        // with no stored id) → a legacy label-based default for categories with no configured style.
        const byId = getMarkerStyleById(this.markerTypeId);
        const byLabel = getMarkerStyle(marker.label);
        const legacy = legacyStyleForLabel(marker.label);
        this.shape = marker.shape || byId.shape || byLabel.shape || legacy.shape;
        this.color = marker.color || byId.color || byLabel.color || legacy.color;

        this.generateMesh();
    }

    generateMesh(){
        const position = new THREE.Vector3(this.transform.position.x, this.transform.position.y, this.transform.position.z);
        const rotation = new THREE.Euler(this.transform.rotation.x, this.transform.rotation.y, this.transform.rotation.z);
        const scale = new THREE.Vector3(this.transform.scale.x, this.transform.scale.y, this.transform.scale.z);

        const geometry = createShapeGeometry(this.shape);
        this.defaultColor = this.color;
        this.generateMeshGroup(geometry, this.color, position, rotation, scale);
    }

    generateMeshGroup(geometry, color, position, rotation, scale){
        // Create semi-transparent material
        const material = new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.3,
            side: THREE.DoubleSide,
        });

        // Create dashed outline material
        const borderMaterial = new THREE.LineDashedMaterial({
            color,
            dashSize: 0.1,
            gapSize: 0.1,
            linewidth: 1,
        });

        // Create the anomaly mesh
        const mesh = new THREE.Mesh(geometry, material);
        mesh.userData.markerId = this.id;

        // Create the outline using edges geometry
        const edges = new THREE.EdgesGeometry(geometry);
        const outline = new THREE.LineSegments(edges, borderMaterial);
        outline.computeLineDistances();

        // Create the group which is the final 3D object.
        const group = new THREE.Group();
        group.add(mesh);
        group.add(outline);
        group.position.copy(position);
        group.rotation.copy(rotation);
        group.scale.copy(scale);

        // Allows us to track groups in the scene by the markerId
        group.userData.markerId = this.id;

        this.meshGroup = markRaw(group);
    }
}

// Default style for markers whose category hasn't been given an explicit shape/colour yet:
// corrosion stands out as a red cube, everything else is a steel-blue sphere (the original
// default look). An admin-configured shape/colour on the category always overrides this.
function legacyStyleForLabel(label){
    if ((label || '').trim().toLowerCase() === 'corrosion') {
        return { shape: 'cube', color: '#FF5E3A' };
    }
    return { shape: DEFAULT_MARKER_SHAPE, color: DEFAULT_MARKER_COLOR };
}
